/**
 * ISO 6346 shipping container number validation.
 *
 * A container number is 4 letters (owner code + equipment category) followed
 * by a 6-digit serial number and a final check digit, e.g. `CSQU3054383`.
 * The check digit is derived from the first 10 characters.
 */

// Letter values per ISO 6346. Multiples of 11 (11, 22, 33) are skipped.
const LETTER_VALUES: Record<string, number> = {
  A: 10, B: 12, C: 13, D: 14, E: 15, F: 16, G: 17, H: 18, I: 19,
  J: 20, K: 21, L: 23, M: 24, N: 25, O: 26, P: 27, Q: 28, R: 29,
  S: 30, T: 31, U: 32, V: 34, W: 35, X: 36, Y: 37, Z: 38,
};

const CONTAINER_PATTERN = /^[A-Z]{4}[0-9]{7}$/;

/**
 * Compute the ISO 6346 check digit for the first 10 characters of a container
 * number (4 letters + 6 digits). Returns null if those characters are not a
 * valid prefix.
 */
export function containerCheckDigit(prefix: string): number | null {
  const normalized = prefix.trim().toUpperCase();
  if (!/^[A-Z]{4}[0-9]{6}$/.test(normalized)) return null;

  let sum = 0;
  for (let i = 0; i < 10; i++) {
    const char = normalized[i];
    const value = i < 4 ? LETTER_VALUES[char] : Number(char);
    sum += value * Math.pow(2, i);
  }

  const checkDigit = sum % 11;
  // A remainder of 10 maps to a check digit of 0.
  return checkDigit === 10 ? 0 : checkDigit;
}

/**
 * Validate a full 11-character ISO 6346 container number, including its check
 * digit. Case-insensitive and tolerant of surrounding whitespace.
 */
export function isValidContainerNumber(value: string): boolean {
  if (!value) return false;
  const normalized = value.trim().toUpperCase();
  if (!CONTAINER_PATTERN.test(normalized)) return false;

  const expected = containerCheckDigit(normalized.slice(0, 10));
  return expected !== null && expected === Number(normalized[10]);
}
